// src/hooks/useAdkRequestResponse.js
import { useState, useCallback, useRef, useEffect } from "react";

const backendUrl = process.env.REACT_APP_NOTIFICATION_BACKEND_URL; // change if needed

export default function useAdkRequestResponse(
  parent_id,
  pediatrician_id,
  child_id,
  languageCode,
  user_type
) {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const recorderRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const sessionIdRef = useRef(Math.random().toString(36).slice(2, 12));

  function pcmToWav(pcmBase64, sampleRate = 24000) {
    const pcm = Uint8Array.from(atob(pcmBase64), (c) => c.charCodeAt(0));

    const wav = new Uint8Array(44 + pcm.length);
    const dv = new DataView(wav.buffer);

    // WAV HEADER
    dv.setUint32(0, 0x52494646, false); // "RIFF"
    dv.setUint32(4, 36 + pcm.length, true);
    dv.setUint32(8, 0x57415645, false); // "WAVE"
    dv.setUint32(12, 0x666d7420, false); // "fmt "
    dv.setUint32(16, 16, true);
    dv.setUint16(20, 1, true); // PCM format
    dv.setUint16(22, 1, true); // mono
    dv.setUint32(24, sampleRate, true);
    dv.setUint32(28, sampleRate * 2, true);
    dv.setUint16(32, 2, true);
    dv.setUint16(34, 16, true);
    dv.setUint32(36, 0x64617461, false); // "data"
    dv.setUint32(40, pcm.length, true);

    wav.set(pcm, 44);
    return new Blob([wav], { type: "audio/wav" });
  }

  const stopCurrentAudio = useCallback(() => {
    if (audioPlayerRef.current) {
      try {
        audioPlayerRef.current.pause();
      } catch {}
      try {
        audioPlayerRef.current.src = "";
      } catch {}
      audioPlayerRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    stopCurrentAudio();
    setCurrentlyPlayingId(null);
    setIsRecording(false);
  }, [stopCurrentAudio]);

  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!reader.result) return resolve("");
        // strip data: prefix
        const base64 = reader.result.toString().replace(/^data:.*;base64,/, "");
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  const b64ToBlob = useCallback((b64Data, contentType = "") => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    const sliceSize = 512;
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  }, []);

  const playAudioUrl = useCallback(
    async (audioUrl,messageId) => {
      stopCurrentAudio();
      setCurrentlyPlayingId(messageId);
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;
      audio.onended = () => {
        try {
          URL.revokeObjectURL(audioUrl);
          setCurrentlyPlayingId(null);
        } catch {}
        if (audioPlayerRef.current === audio) audioPlayerRef.current = null;
      };
      try {
        await audio.play();
      } catch (err) {
        console.warn("Playback prevented:", err);
        try {
          await new Promise((r) => setTimeout(r, 200));
          await audio.play();
        } catch {}
      }
    },
    [stopCurrentAudio]
  );

  const sendRequest = useCallback(
    async (payload, isAudioRequest) => {
      if (!backendUrl) return;
      setIsTyping(true);
      const requestUrl = isAudioMode ? `${backendUrl}/api/chat` : `${backendUrl}/api/text`;
      try {
        const response = await fetch(requestUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            parent_id,
            pediatrician_id,
            child_id,
            is_audio: isAudioRequest,
            language_code: languageCode,
            user_type,
            ...payload,
          }),
        });

        if (!response.ok) {
          let errText = "Failed to get response from server";
          try {
            const errorData = await response.json();
            errText = errorData.detail || errText;
          } catch {}
          throw new Error(errText);
        }

        const responseData = await response.json();

        const userText =
          payload.message || (isAudioRequest ? "🎤 voice message" : "");
        if (userText) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + "-user",
              role: "user",
              text: userText,
              hasAudio: isAudioRequest,
            },
          ]);
        }

        const messageId = Date.now() + "-model";
        const hasAudio = !!responseData.response_audio_base64;
        const modelMessage = {
          id: messageId,
          role: "model",
          hasAudio,
          text: hasAudio
            ? ""
            : responseData.response_text || "Error: Empty Response",
        };

        if (hasAudio) {
          // const blob = b64ToBlob(responseData.response_audio_base64, mime);
          // const url = URL.createObjectURL(blob);
          const pcmB64 = responseData.response_audio_base64;
          const sr = responseData.sample_rate || 24000;
          const wavBlob = pcmToWav(pcmB64, sr);
          const url = URL.createObjectURL(wavBlob);
          playAudioUrl(url,messageId).catch((e) => console.warn("playAudioUrl error", e));
          // show transcript if available
          if (responseData.response_text)
            modelMessage.text = responseData.response_text;
        }

        setMessages((prev) => [...prev, modelMessage]);
      } catch (error) {
        console.error("ADK Request failed:", error);
        setMessages((prev) => [
          ...prev,
          { id: Date.now(), role: "error", text: `Error: ${error.message}` },
        ]);
      } finally {
        setIsTyping(false);
      }
    },
    [
      playAudioUrl,
      parent_id,
      pediatrician_id,
      child_id,
      languageCode,
      user_type,
      isAudioMode
    ]
  );

  // Public API
  const sendText = useCallback(
    (text) => sendRequest({ message: text }, false),
    [sendRequest]
  );

  const startVoice = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Microphone access is not supported by your browser.");
      return;
    }
    try {
      stopAudio();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      recorderRef.current = { recorder: mediaRecorder, chunks: [], stream };

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recorderRef.current.chunks.push(e.data);
      };

      mediaRecorder.start();
      setIsAudioMode(true);
      setIsRecording(true);
    } catch (e) {
      console.error("Error starting microphone:", e);
      alert("Could not access microphone. Please check your permissions.");
      setIsRecording(false);
    }
  }, [stopAudio]);

  const stopVoice = useCallback(() => {
    const cur = recorderRef.current;
    if (!cur || !cur.recorder) return;
    const { recorder, chunks, stream } = cur;
    setIsRecording(false);

    recorder.onstop = async () => {
      try {
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size === 0) {
          setMessages((prev) => [
            ...prev,
            { id: Date.now(), role: "error", text: "Empty audio recording." },
          ]);
          return;
        }
        const base64 = await blobToBase64(blob);
        await sendRequest({ audio_data: base64 }, true);
      } catch (err) {
        console.error("Error converting/sending audio:", err);
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            role: "error",
            text: "Error processing audio recording.",
          },
        ]);
      } finally {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
        recorderRef.current = null;
        setIsAudioMode(false);
      }
    };

    try {
      recorder.stop();
    } catch (e) {
      /* ignore */
    }
  }, [blobToBase64, sendRequest]);

  const reconnect = () => setIsConnected(true);

  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current?.recorder) recorderRef.current.recorder.stop();
      } catch {}
      stopCurrentAudio();
    };
  }, [stopCurrentAudio]);

  return {
    messages,
    isTyping,
    isConnected,
    isAudioMode,
    isRecording,
    startVoice,
    stopVoice,
    sendText,
    reconnect,
    currentlyPlayingId,
    sessionId: sessionIdRef.current,
  };
}
